import { Check, Link2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface CopyKeyProps {
  /** The display key, e.g. `PAY-12`. */
  children: ReactNode;
  /** In-app path of the record the key names. */
  path: string;
  className?: string;
}

/**
 * The display key of a record, which copies a link to it.
 *
 * The key is what people quote in chat and tickets, so clicking it hands over
 * the whole address rather than the key alone. The clipboard is unavailable
 * over plain HTTP in some browsers, so a failure says so instead of silently
 * doing nothing.
 */
export const CopyKey = ({ children, path, className }: CopyKeyProps) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('common.linkCopied'));
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('common.linkCopyFailed'));
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={t('common.copyLink')}
      // The confirmation is an icon for the eye and a name change for everyone
      // else, so the result of the click is never invisible.
      aria-label={
        copied ? t('common.linkCopied') : t('common.copyLinkTo', { key: String(children) })
      }
      className={cn(
        'inline-flex items-center gap-1.5 rounded bg-sunken px-1.5 py-0.5',
        'font-mono text-[12px] text-ink-subtle transition-colors hover:text-ink',
        className,
      )}
    >
      {children}
      {copied ? (
        <Check className="size-3 text-pass" aria-hidden="true" />
      ) : (
        <Link2 className="size-3" aria-hidden="true" />
      )}
    </button>
  );
};
